import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  MdCheckCircle,
  MdDescription,
  MdLock,
  MdOpenInNew,
  MdPictureAsPdf,
} from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { getPdf, signRgpd } from '../../services/rgpdService';
import CopyrightInfo from '../Common/CopyrightInfo';

export default function RGPDViewPdf({ onSigned }) {
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [checked, setChecked] = useState(false);
  const [signing, setSigning] = useState(false);

  const { token } = useParams();

  // 🔹 Carrega PDF
  const loadPdf = async () => {
    setLoading(true);
    try {
      const res = await getPdf(token);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('[RGPD] Erro ao carregar PDF:', err);
      toast.error('Não foi possível carregar o documento.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPdf();
    return () => pdfUrl && URL.revokeObjectURL(pdfUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 🔹 Assinatura digital
  const handleSign = async () => {
    setSigning(true);
    try {
      await signRgpd(token);
      toast.success('RGPD assinado com sucesso!');
      setSigned(true);
      setTimeout(() => onSigned?.(), 1200);
    } catch (err) {
      console.error('[RGPD] Erro ao assinar:', err);
      toast.error('Ocorreu um erro ao concluir a assinatura.');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className='text-center'>
      {/* Cabeçalho */}
      <div className='flex justify-center items-center rounded-md bg-green-100 py-3 px-6 text-green-800 mb-6'>
        <MdDescription className='h-5 w-5 text-green-700 mr-2' />
        <span className='font-bold text-base md:text-base text-xs'>
          Confirme a leitura e assinatura da sua minuta RGPD
        </span>
      </div>

      {/* Card principal */}
      {!signed ? (
        <div className='w-full max-w-md mx-auto bg-white border border-gray-300 rounded-lg shadow-sm p-6 text-center flex flex-col items-center justify-center'>
          {loading ? (
            <p className='text-gray-600 mt-4'>A carregar o documento...</p>
          ) : (
            <>
              {/* Ícone */}
              <MdPictureAsPdf className='text-red-600 h-12 w-12 mb-3' />

              {/* Nome do ficheiro */}
              <span className='font-medium text-gray-800 mb-1'>
                Minuta_RGPD.pdf
              </span>

              {/* Link Ler Documento */}
              <a
                href={pdfUrl}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() =>
                  toast('O documento será aberto numa nova aba.', {
                    icon: '📖',
                    duration: 3000,
                  })
                }
                className='flex items-center justify-center gap-1 text-[#004775] text-sm underline hover:text-[#0C92AF] mb-4'
              >
                <MdOpenInNew className='h-4 w-4' />
                Ler Documento
              </a>

              {/* Checkbox */}
              <div className='flex items-center justify-center'>
                <input
                  id='confirmRead'
                  type='checkbox'
                  checked={checked}
                  disabled={signing}
                  onChange={() => setChecked(!checked)}
                  className='mr-2 w-4 h-4 accent-[#004775]'
                />
                <label
                  htmlFor='confirmRead'
                  className='text-sm text-gray-700 select-none'
                >
                  Confirmo que li e compreendi o conteúdo do documento RGPD.
                </label>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className='mt-6 flex flex-col items-center justify-center text-green-700 font-semibold'>
          <MdLock className='h-6 w-6 mb-2' />
          <span>Documento assinado digitalmente com sucesso.</span>
        </div>
      )}

      {/* Botão principal */}
      {!signed && (
        <button
          onClick={handleSign}
          disabled={!checked || loading || signing}
          className={`mt-6 inline-flex items-center justify-center px-6 py-3 rounded-md font-medium transition-colors ${
            !checked || loading || signing
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-[#004775] text-white hover:bg-[#0C92AF]'
          }`}
        >
          <MdCheckCircle className='inline-block mr-2' />
          {signing ? 'A enviar...' : 'Confirmar e Assinar Digitalmente'}
        </button>
      )}

      {/* Rodapé */}
      <div className='mt-10 text-gray-600 text-sm'>
        O documento assinado será registado automaticamente no seu processo.
      </div>

      <CopyrightInfo className='mt-10' />
    </div>
  );
}
